import type {
  DataCollectionSubmissionStatus,
  ImportSubmissionResponse,
  ReportingPeriod,
} from "@entrilia/shared/api/dataCollectionTypes";
import type { DataSubmissionLayout, ResolvedBlockContent } from "@entrilia/shared/api/inputFormTypes";
import type { RangeRequestParams } from "@entrilia/shared/api/types";
import type { ObjectFieldSort } from "./objectTypes";

export interface DataCollectionSubmissionInfo {
  id: string;
  recipientName: string;
  isRecipientMissing: boolean;
  status: DataCollectionSubmissionStatus;
  auditory: DataCollectionRequestAuditory;
  requestTemplateId: string;
  requestTemplateName: string;
  reportingDate: string;
  dueDate: string;
  submittedBy?: string;
  submittedAt?: string;
  scheduledToBePublishedAt?: string;
}

export interface DataCollectionSubmissionDetails {
  id: string;
  dataCollectionRequestId: string;
  requestName: string;
  recipientObjectId: string;
  recipientName: string;
  status: DataCollectionSubmissionStatus;
  auditory: DataCollectionRequestAuditory;
  requestTemplateId: string;
  requestTemplateName: string;
  reportingDate: string;
  dueDate: string;
  submittedBy?: string;
  submittedAt?: string;
  changesRequestedBy?: string;
  changesRequestedAt?: string;
  lastReminderSentAt?: string;
  scheduledToBePublishedAt?: string;
  blockContents: ResolvedBlockContent[];
  layout: DataSubmissionLayout;
  canBeSubmitted: boolean;
}

export interface DataSubmissionConfiguration {
  id: string;
  name: string;
  scenario?: string;
  isScenarioConfigurable: boolean;
  reportingPeriod: ReportingPeriod;
}

export interface DataCollectionRequestInfo {
  id: string;
  name: string;
  templateId: string;
  templateName: string;
  auditory: DataCollectionRequestAuditory;
  status: DataCollectionRequestStatus;
  reportingDate: string;
  createdAt: string;
  publishedAt?: string;
  scheduledToBePublishedAt?: string;
  submittedSubmissions: number;
  totalSubmissions: number;
  contactCategories: string[];
  internalUserPermissionCategories: string[];
}

export interface DataCollectionRequestDetails {
  id: string;
  name: string;
  templateId: string;
  templateName: string;
  auditory: DataCollectionRequestAuditory;
  status: DataCollectionRequestStatus;
  reportingDate: string;
  createdAt: string;
  publishedAt?: string;
  scheduledToBePublishedAt?: string;
  submittedSubmissions: number;
  submissions: RequestSubmissionInfo[];
  scope: DataCollectionRequestScope;
  templateVersion: number;
  scenarioId: string;
  scenarioName: string;
  templateReportingPeriod: ReportingPeriod;
  recipientObjectIds: string[];
  dueDate: string;
  contactCategories: string[];
  internalUserPermissionCategories: string[];
}

export interface RequestSubmissionInfo {
  id: string;
  status: DataCollectionSubmissionStatus;
  dueDate: string;
  recipientObjectId: string;
  recipientName: string;
  isRecipientMissing: boolean;
  auditoryList: RequestSubmissionAuditory[];
  submittedAt?: string;
  submittedByUserName?: string;
  lastReminderSentAt?: string;
}

export interface SearchSubmissionsRequest {
  range: RangeRequestParams;
  searchTerm?: string;
  requestTemplateIds?: string[];
  recipientObjectIds?: string[];
  statuses?: DataCollectionSubmissionStatus[];
  auditory?: DataCollectionRequestAuditory[];
  reportingDateFrom?: string;
  reportingDateTo?: string;
}

export interface RequestSubmissionAuditory {
  id: string;
  name: string;
}

export const allDataCollectionRequestAuditoryValues = ["PortfolioCompany", "Internal"] as const;
export type DataCollectionRequestAuditory = (typeof allDataCollectionRequestAuditoryValues)[number];

export const allDataCollectionRequestStatuses = ["Draft", "InProgress", "Scheduled", "Completed"] as const;
export type DataCollectionRequestStatus = (typeof allDataCollectionRequestStatuses)[number];

export interface SearchDataCollectionRequestRequest {
  range: RangeRequestParams;
  searchTerm?: string;
  templateIds?: string[];
  statuses?: DataCollectionRequestStatus[];
  auditory?: DataCollectionRequestAuditory[];
  reportingDateFrom?: string;
  reportingDateTo?: string;
  sort?: ObjectFieldSort;
}

export interface CreateDataCollectionRequestRequest {
  templateId: string;
  name: string;
  reportingDate: string;
  dueDate: string;
  scenarioId?: string;
}

export interface UpdateDataCollectionRequestRequest {
  name?: string;
  reportingDate?: string;
  dueDate?: string;
}

export interface DataCollectionRequestTemplateVersion {
  id: string;
  name: string;
  version: number;
  templateId: string;
  scenarioName?: string;
  scenarioId?: string;
  isScenarioConfigurable: boolean;
  auditory: DataCollectionRequestAuditory;
  recipientObjectIds: string[];
  reportingPeriod: ReportingPeriod;
  dataSubmissionConfigurationId: string;
  dataSubmissionConfigurationName: string;
  scope: DataCollectionRequestScope;
  contactCategories: string[];
  internalUserPermissionCategories: string[];
}

type DataCollectionRequestScope = "PortfolioMonitoring";

export interface CreateDataCollectionRequestTemplateVersionRequest {
  templateId?: string;
  objectType: string;
  configurationId: string;
  name: string;
  auditory: DataCollectionRequestAuditory;
  objectAccessCategoryIds: string[];
  recipientObjectIds: string[];
}

export type CreateDataCollectionRequestTemplateVersionResponse =
  | {
      templateVersion: DataCollectionRequestTemplateVersion;
      success: true;
      errors: [];
    }
  | {
      templateVersion?: undefined;
      success: false;
      errors: string[];
    };

export interface RequestSubmissionOperationRequest {
  submissionIds: string[];
  sendEmailNotifications?: boolean;
  commentUserInput?: string;
}

export interface UpdateRequestSubmissionsRequest {
  submissionIds: string[];
  dueDate?: string;
}

export interface AddRequestSubmissionsRequest {
  recipientObjectIds: string[];
}

export type ImportSubmissionBlockInputResponse = ImportSubmissionResponse<DataCollectionSubmissionDetails>;

export interface PublishRequestRequest {
  sendEmailNotifications: boolean;
  scheduledToBePublishedAt?: string;
  commentUserInput?: string;
}

export interface UpdateMultipleMetricExtensionsRequest {
  objectId: string;
  extensionUpdates: {
    id: string;
    hidden?: boolean;
  }[];
}

export interface RenameMetricExtensionRequest {
  extensionId: string;
  newValue: string;
}
